import React, { useState, useEffect } from 'react';
import { billingService } from '../../../services/billingService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const BillingHistoryCard = ({ userId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      loadHistory();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try orders table first
      const ordersResult = await billingService?.getBillingHistory(userId, { limit: 5 });
      
      if (ordersResult?.data?.length > 0) {
        const mapped = ordersResult?.data?.map(item => ({
          id: item?.id,
          date: item?.created_at,
          description: item?.order_items?.[0]?.subscription_plans?.name || `Order #${item?.order_number || item?.id?.slice(0, 8)}`,
          amount: item?.total_amount,
          currency: item?.currency || 'USD',
          status: item?.status,
          type: 'order',
          raw: item
        }));
        setHistory(mapped);
        return;
      }

      // Fallback: try payment_transactions table
      if (supabase) {
        const { data: txData, error: txError } = await supabase
          ?.from('payment_transactions')
          ?.select('*')
          ?.eq('user_id', userId)
          ?.order('created_at', { ascending: false })
          ?.limit(5);

        if (!txError && txData?.length > 0) {
          const mapped = txData?.map(tx => ({
            id: tx?.id,
            date: tx?.created_at,
            description: `Payment via ${tx?.gateway || 'Stripe'}`,
            amount: tx?.amount,
            currency: tx?.currency || 'USD',
            status: tx?.status,
            type: 'transaction',
            raw: tx
          }));
          setHistory(mapped);
          return;
        }
      }

      setHistory([]);
    } catch (err) {
      console.error('Error loading billing history:', err);
      setError(err?.message);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency = 'USD') => {
    if (amount == null) return '$0.00';
    const num = typeof amount === 'number' ? amount : parseFloat(amount);
    // If amount looks like cents (very large), divide by 100
    const value = num > 10000 ? num / 100 : num;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency })?.format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'succeeded':
        return 'bg-green-100 text-green-700';
      case 'pending': case'processing':
        return 'bg-yellow-100 text-yellow-700';
      case 'failed': case'cancelled':
        return 'bg-red-100 text-red-700';
      case 'refunded':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const downloadInvoice = (item) => {
    const invoiceHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice - LeadGen Pro AI</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; color: #333; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 2px solid #6366f1; padding-bottom: 20px; }
          .company-name { font-size: 24px; font-weight: bold; color: #6366f1; }
          .invoice-title { font-size: 32px; font-weight: bold; color: #333; text-align: right; }
          .invoice-meta { text-align: right; margin-top: 8px; color: #666; font-size: 14px; }
          .details { display: flex; justify-content: space-between; margin: 30px 0; }
          .detail-block h4 { font-size: 12px; text-transform: uppercase; color: #999; margin-bottom: 8px; }
          .detail-block p { margin: 4px 0; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin: 30px 0; }
          th { background: #f5f5f5; padding: 12px; text-align: left; font-size: 13px; text-transform: uppercase; color: #666; }
          td { padding: 12px; border-bottom: 1px solid #eee; font-size: 14px; }
          .total-row td { font-weight: bold; font-size: 16px; border-top: 2px solid #333; }
          .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; background: #d1fae5; color: #065f46; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #999; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="company-name">LeadGen Pro AI</div>
            <p style="color:#666;font-size:14px;margin-top:4px;">leadgenproai.us</p>
          </div>
          <div>
            <div class="invoice-title">INVOICE</div>
            <div class="invoice-meta">
              <div>Invoice #: ${item?.id?.slice(0, 8)?.toUpperCase()}</div>
              <div>Date: ${formatDate(item?.date)}</div>
            </div>
          </div>
        </div>
        <div class="details">
          <div class="detail-block">
            <h4>Billed To</h4>
            <p>LeadGen Pro AI Customer</p>
          </div>
          <div class="detail-block">
            <h4>Payment Status</h4>
            <span class="status-badge">${item?.status || 'N/A'}</span>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Date</th>
              <th style="text-align:right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${item?.description}</td>
              <td>${formatDate(item?.date)}</td>
              <td style="text-align:right">${formatAmount(item?.amount, item?.currency)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td colspan="2">Total</td>
              <td style="text-align:right">${formatAmount(item?.amount, item?.currency)}</td>
            </tr>
          </tfoot>
        </table>
        <div class="footer">
          <p>Thank you for your business! For questions, contact support@leadgenproai.us</p>
        </div>
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    if (win) {
      win?.document?.write(invoiceHtml);
      win?.document?.close();
      win?.print();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Receipt" size={20} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Recent Billing</h3>
        </div>
        {history?.length > 0 && (
          <span className="text-xs text-muted-foreground">{history?.length} record{history?.length !== 1 ? 's' : ''}</span>
        )}
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="animate-pulse flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded w-40"></div>
                <div className="h-3 bg-muted rounded w-24"></div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="h-4 bg-muted rounded w-16"></div>
                <div className="h-6 bg-muted rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-2 opacity-60" />
          <p className="text-sm text-muted-foreground">Failed to load billing history</p>
          <button onClick={loadHistory} className="mt-2 text-xs text-primary hover:underline">Try again</button>
        </div>
      ) : history?.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Receipt" size={28} className="text-muted-foreground opacity-50" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">No billing history yet</p>
          <p className="text-xs text-muted-foreground mt-1">Your invoices and payment records will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3">Date</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3">Description</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3">Amount</th>
                <th className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3">Status</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3">Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {history?.map((item, index) => (
                <tr key={item?.id || index} className="hover:bg-muted/20 transition-colors">
                  <td className="py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDate(item?.date)}</td>
                  <td className="py-3 text-sm font-medium text-foreground">{item?.description}</td>
                  <td className="py-3 text-sm font-bold text-foreground text-right whitespace-nowrap">{formatAmount(item?.amount, item?.currency)}</td>
                  <td className="py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusStyle(item?.status)}`}>
                      {item?.status || 'N/A'}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <button
                      onClick={() => downloadInvoice(item)}
                      className="inline-flex items-center space-x-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                      title="Download Invoice"
                    >
                      <Icon name="Download" size={14} />
                      <span>PDF</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BillingHistoryCard;